import React, { useState } from 'react';
import { Table, Card, CardBody, Collapse, Badge, Button, Input } from 'reactstrap';
import { formatCurrency } from '../../../../utils/format';
import { formatDate } from '../../../../utils/date';
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from '../../../../constants/financial';

/**
 * CashFlowTable
 * Card único: filtros de período + resumo inline + tabela de fluxo de caixa dia a dia.
 * Segue o padrão visual de ReceivableTable.
 */
export const CashFlowTable = ({
    projectedDays,
    loading,
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    currentTotalBalance,
    bankAccountsCount,
}) => {
    const [expandedDays, setExpandedDays] = useState({});

    const toggleRow = (dateKey) => {
        setExpandedDays(prev => ({
            ...prev,
            [dateKey]: !prev[dateKey]
        }));
    };

    // Totais para o resumo
    const totals = projectedDays.reduce(
        (acc, day) => ({
            income: acc.income + day.income,
            expense: acc.expense + day.expense,
        }),
        { income: 0, expense: 0 }
    );

    const lastProjectedBalance = projectedDays.length > 0
        ? projectedDays[projectedDays.length - 1].projectedBalance
        : currentTotalBalance;

    const getStatusBadge = (status) => {
        const map = {
            [FINANCIAL_STATUS.PAID]: { label: 'Pago', color: 'success' },
            [FINANCIAL_STATUS.PENDING]: { label: 'Pendente', color: 'warning' },
            [FINANCIAL_STATUS.OVERDUE]: { label: 'Atrasado', color: 'danger' },
        };
        const s = map[status] || map[FINANCIAL_STATUS.PENDING];
        return <Badge color={s.color} className="px-1 py-0" style={{ fontSize: "0.65rem" }}>{s.label}</Badge>;
    };

    return (
        <Card className="border-0 shadow-sm">
            <CardBody className="p-0">
                {/* ── Header: Filtros (esq) + Resumo (dir) ── */}
                <div className="d-flex align-items-center flex-wrap gap-2 px-3 py-3" style={{ borderBottom: "1px solid #e9ecef" }}>
                    {/* Filtro de período */}
                    <div className="d-flex align-items-center gap-1">
                        <Input
                            bsSize="sm"
                            type="date"
                            value={startDate}
                            onChange={(e) => onStartDateChange(e.target.value)}
                            style={{ width: "140px" }}
                        />
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>até</span>
                        <Input
                            bsSize="sm"
                            type="date"
                            value={endDate}
                            onChange={(e) => onEndDateChange(e.target.value)}
                            min={startDate}
                            style={{ width: "140px" }}
                        />
                    </div>

                    {/* Resumo à direita */}
                    <div className="d-flex align-items-center gap-3 ms-auto">
                        <div className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-bank-outline" style={{ color: "#1565c0", fontSize: "0.9rem" }} />
                            <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Saldo Atual</span>
                            <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#1565c0" }}>
                                {formatCurrency(currentTotalBalance)}
                                <small className="text-muted ms-1">({bankAccountsCount})</small>
                            </span>
                        </div>
                        <div style={{ width: 1, height: 18, background: "#e0e0e0" }} />
                        <div className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-arrow-up-bold" style={{ color: "#2e7d32", fontSize: "0.9rem" }} />
                            <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Entradas</span>
                            <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#2e7d32" }}>
                                {formatCurrency(totals.income)}
                            </span>
                        </div>
                        <div style={{ width: 1, height: 18, background: "#e0e0e0" }} />
                        <div className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-arrow-down-bold" style={{ color: "#d32f2f", fontSize: "0.9rem" }} />
                            <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Saídas</span>
                            <span className="fw-bold" style={{ fontSize: "0.82rem", color: "#d32f2f" }}>
                                {formatCurrency(totals.expense)}
                            </span>
                        </div>
                        <div style={{ width: 1, height: 18, background: "#e0e0e0" }} />
                        <div className="d-flex align-items-center gap-1">
                            <i className="mdi mdi-chart-line" style={{ color: lastProjectedBalance < 0 ? "#d32f2f" : "#1565c0", fontSize: "0.9rem" }} />
                            <span style={{ fontSize: "0.78rem", color: "#78909c" }}>Projetado</span>
                            <span className="fw-bold" style={{ fontSize: "0.82rem", color: lastProjectedBalance < 0 ? "#d32f2f" : "#1565c0" }}>
                                {formatCurrency(lastProjectedBalance)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Tabela ── */}
                {!projectedDays.length && !loading ? (
                    <div className="text-center py-5">
                        <i className="mdi mdi-chart-timeline-variant text-muted" style={{ fontSize: "3rem" }} />
                        <h6 className="mt-3 text-muted">Nenhum movimento encontrado</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                            Ajuste o período ou aguarde novos lançamentos.
                        </p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table className="table-hover align-middle mb-0">
                            <thead>
                                <tr style={{ background: "#f8f9fa" }}>
                                    <th style={thStyle}>Data</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Entradas</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Saídas</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Líquido</th>
                                    <th style={{ ...thStyle, textAlign: "right", paddingRight: '1.5rem' }}>Saldo Projetado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projectedDays.map((day) => (
                                    <React.Fragment key={day.date}>
                                        <tr
                                            onClick={() => toggleRow(day.date)}
                                            style={{ cursor: 'pointer', background: day.isOverdue ? '#fff1f2' : 'transparent' }}
                                        >
                                            <td style={tdStyle}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <Button size="sm" color="link" className="p-0 text-muted">
                                                        <i className={`mdi mdi-chevron-${expandedDays[day.date] ? 'down' : 'right'} fs-5`} />
                                                    </Button>
                                                    {day.isOverdue ? (
                                                        <Badge color="danger" className="px-2 py-1" style={{ fontSize: "0.72rem" }}>
                                                            {day.displayDate}
                                                        </Badge>
                                                    ) : (
                                                        <span style={{ fontSize: "0.85rem" }}>{day.displayDate}</span>
                                                    )}
                                                    {day.records.length > 0 && (
                                                        <small className="text-muted">({day.records.length})</small>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: "right", color: '#2e7d32', fontWeight: 600 }}>
                                                {day.income > 0 ? formatCurrency(day.income) : '—'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: "right", color: '#d32f2f', fontWeight: 600 }}>
                                                {day.expense > 0 ? formatCurrency(day.expense) : '—'}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: (day.income - day.expense) >= 0 ? '#2e7d32' : '#d32f2f' }}>
                                                {(day.income - day.expense) >= 0 ? '+' : ''}{formatCurrency(day.income - day.expense)}
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                textAlign: "right",
                                                fontWeight: 700,
                                                paddingRight: '1.5rem',
                                                color: day.projectedBalance < 0 ? '#d32f2f' : '#1e293b'
                                            }}>
                                                {formatCurrency(day.projectedBalance)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="5" className="p-0 border-0">
                                                <Collapse isOpen={expandedDays[day.date]}>
                                                    <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
                                                        {day.records.length === 0 && <small className="text-muted">Sem movimentos neste dia.</small>}
                                                        {day.records.map(record => (
                                                            <div key={record.id} className="d-flex justify-content-between align-items-center py-2" style={{ borderBottom: '1px dashed #cbd5e1' }}>
                                                                <div>
                                                                    <span className="fw-medium d-block text-dark" style={{ fontSize: '0.82rem' }}>
                                                                        {record.description || 'Sem descrição'}
                                                                        <span className="ms-2">{getStatusBadge(record.status)}</span>
                                                                    </span>
                                                                    <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                                        {record.entityName || 'N/A'}
                                                                        {record.categoryName && <> • {record.categoryName}</>}
                                                                        {record.dueDate && <> • Venc: {formatDate(record.dueDate)}</>}
                                                                    </span>
                                                                </div>
                                                                <div className="fw-semibold" style={{ color: record.type === FINANCIAL_TYPES.INCOME ? '#2e7d32' : '#d32f2f', fontSize: '0.82rem' }}>
                                                                    {record.type === FINANCIAL_TYPES.INCOME ? '+' : '-'} {formatCurrency(record.netAmount)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

const thStyle = {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#78909c",
    padding: "10px 12px",
    borderBottom: "2px solid #e9ecef",
    whiteSpace: "nowrap",
};

const tdStyle = {
    fontSize: "0.85rem",
    padding: "10px 12px",
    verticalAlign: "middle",
};
